import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../../../core/components/ConfirmModal.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../../core/components/Navigation/internal/queries';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { url } from '../../../../core/utils/link';
import { mapDocumentTypeToGqlDocumentType } from '../../../constants.ts';
import { IChannel } from '../../../stores/channelStore.tsx';
import { useOverviewContext } from './OverviewContext';

const DELETE_CHANNEL = gql`
    mutation DeleteChannel($channelId: UUID!, $documentType: DocumentTypeEnum!) {
        deleteChannel(channelId: $channelId, documentType: $documentType) {
            success
        }
    }
`;

interface IProps {
    channel: IChannel;
    onClose: () => void;
}

const DeleteChannelModal = ({ channel, onClose }: IProps) => {
    const { t } = useTranslation('assistance');
    const { documentConfiguration } = useOverviewContext();
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const [deleteChannel, { loading }] = useMutation(DELETE_CHANNEL, {
        refetchQueries: [
            { query: GET_NAVIGATION_CHANNELS },
            { query: documentConfiguration.GET_CHANNELS_OVERVIEW_DATA },
        ],
        awaitRefetchQueries: true,
    });

    const handleConfirm = async () => {
        try {
            await deleteChannel({
                variables: {
                    channelId: channel.id,
                    documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
                },
            });
            publishToast({ description: t('overview.deleteChannelModal.success'), status: 'success' });
            navigate(url(documentConfiguration.constants.OVERVIEW_PATH), { replace: true });
        } catch {
            publishToast({ description: t('overview.deleteChannelModal.error'), status: 'error' });
        }
        onClose();
    };

    return (
        <ConfirmModal
            danger
            title={t('overview.deleteChannelModal.title')}
            labelConfirm={t('overview.deleteChannelModal.confirm')}
            confirmDisabled={loading}
            onCancel={onClose}
            onConfirm={handleConfirm}
        >
            {t('overview.deleteChannelModal.description', { name: channel.name })}
        </ConfirmModal>
    );
};

export default DeleteChannelModal;
